import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Shield,
  Smartphone,
  Key,
  QrCode,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Download
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminSecurityMFA() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadMFAStatus();
    }
  }, [user]);

  const loadMFAStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/mfa/status?user_id=${user.id}`);
      const data = await response.json();
      setMfaStatus(data);
    } catch (error) {
      console.error("Failed to load MFA status:", error);
      toast.error("Failed to load MFA status");
    } finally {
      setLoading(false);
    }
  };

  const enableMFA = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/mfa/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          user_email: user.email,
          method: "totp"
        })
      });
      
      if (!response.ok) throw new Error("Failed to enable MFA");
      
      const data = await response.json();
      setSetupData(data);
      setBackupCodes(data.backup_codes || []);
      toast.success("MFA setup initiated. Please verify to complete.");
    } catch (error) {
      toast.error("Failed to enable MFA");
    }
  };

  const verifyMFA = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/mfa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          code: verificationCode
        })
      });
      
      if (!response.ok) throw new Error("Invalid code");
      
      toast.success("MFA enabled successfully!");
      setSetupData(null);
      setVerificationCode("");
      loadMFAStatus();
    } catch (error) {
      toast.error("Invalid verification code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const disableMFA = async () => {
    if (disableCode.length !== 6) {
      toast.error("Please enter a valid code");
      return;
    }
    
    setDisabling(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/mfa/disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          code: disableCode
        })
      });
      
      if (!response.ok) throw new Error("Invalid code");
      
      toast.success("MFA disabled");
      setShowDisableDialog(false);
      setDisableCode("");
      loadMFAStatus();
    } catch (error) {
      toast.error("Invalid code. MFA not disabled.");
    } finally {
      setDisabling(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `Speakr MFA Backup Codes
Generated: ${new Date().toISOString()}
User: ${user.email}

IMPORTANT: Store these codes securely. Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app, use one of these codes to sign in.
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'speakr-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="mfa-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Two-Factor Authentication</h1>
          <p className="text-muted-foreground">
            Add an extra layer of security to your admin account
          </p>
        </div>
        <Badge 
          variant={mfaStatus?.verified ? "default" : "secondary"}
          className={mfaStatus?.verified ? "bg-emerald-500" : ""}
        >
          <Shield className="w-3 h-3 mr-1" />
          {mfaStatus?.verified ? "Protected" : "Not Configured"}
        </Badge>
      </div>

      {/* MFA Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Authenticator App
          </CardTitle>
          <CardDescription>
            Use an authenticator app like Google Authenticator, Authy, or 1Password
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!mfaStatus?.enabled && !setupData && (
            <div className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>MFA Not Enabled</AlertTitle>
                <AlertDescription>
                  Your account is not protected by two-factor authentication. 
                  We strongly recommend enabling MFA for enhanced security.
                </AlertDescription>
              </Alert>
              <Button onClick={enableMFA} data-testid="enable-mfa-btn">
                <Lock className="w-4 h-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}

          {setupData && !mfaStatus?.verified && (
            <div className="space-y-6">
              {/* QR Code Section */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg flex items-center justify-center">
                    <img 
                      src={setupData.qr_code} 
                      alt="MFA QR Code" 
                      className="w-48 h-48"
                      data-testid="mfa-qr-code"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Scan this QR code with your authenticator app
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Manual Entry Key</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 px-3 py-2 bg-muted rounded font-mono text-sm break-all">
                        {setupData.secret}
                      </code>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => copyToClipboard(setupData.secret, 'secret')}
                      >
                        {copiedCode === 'secret' ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Enter verification code</Label>
                    <InputOTP
                      maxLength={6}
                      value={verificationCode}
                      onChange={setVerificationCode}
                      data-testid="mfa-verify-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                    <p className="text-xs text-muted-foreground">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  <Button 
                    onClick={verifyMFA} 
                    disabled={verifying || verificationCode.length !== 6}
                    className="w-full"
                    data-testid="verify-mfa-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Verify & Enable MFA
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Backup Codes */}
              {backupCodes.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Backup Codes
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Save these codes in a secure place. Each can only be used once.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadBackupCodes}>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {backupCodes.map((code, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between px-3 py-2 bg-muted rounded-lg font-mono text-sm"
                      >
                        <span>{code}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(code, index)}
                        >
                          {copiedCode === index ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <Alert className="mt-4" variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Important:</strong> These codes will only be shown once. 
                      Store them securely before closing this page.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          )}

          {mfaStatus?.verified && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border border-emerald-200 dark:border-emerald-900">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                    MFA is Active
                  </p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-500">
                    Your account is protected with two-factor authentication
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowBackupCodes(true)}
                >
                  <Key className="w-4 h-4 mr-2" />
                  View Backup Codes
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => setShowDisableDialog(true)}
                  data-testid="disable-mfa-btn"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Disable MFA
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Security Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                mfaStatus?.verified 
                  ? "bg-emerald-100 text-emerald-600" 
                  : "bg-amber-100 text-amber-600"
              }`}>
                {mfaStatus?.verified ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
              </div>
              <div>
                <p className="font-medium">Enable two-factor authentication</p>
                <p className="text-sm text-muted-foreground">
                  Protect your account with a secondary verification method
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                <Check className="w-3 h-3" />
              </div>
              <div>
                <p className="font-medium">Use a strong, unique password</p>
                <p className="text-sm text-muted-foreground">
                  Never reuse passwords across different services
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                <Check className="w-3 h-3" />
              </div>
              <div>
                <p className="font-medium">Review active sessions regularly</p>
                <p className="text-sm text-muted-foreground">
                  Revoke access from devices you don't recognize
                </p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Disable MFA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              This will remove the extra security layer from your account.
              Enter your current MFA code to confirm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Enter your 6-digit code</Label>
            <InputOTP
              maxLength={6}
              value={disableCode}
              onChange={setDisableCode}
              className="mt-2"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={disableMFA}
              disabled={disabling || disableCode.length !== 6}
            >
              {disabling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable MFA"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
